import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosClient from "../api/AxiosClient";

// FastAPI error responses are { detail: string } (an object), not a plain
// string — render that object directly and React throws "Objects are not
// valid as a React child". Always unwrap to a string before it hits state.
function extractErrorMessage(err: any, fallback: string): string {
  const data = err?.response?.data;
  if (typeof data === "string") return data;
  if (data && typeof data === "object" && typeof data.detail === "string") {
    return data.detail;
  }
  return err?.message || fallback;
}

export interface ManagedAgent {
  id: number;
  agent_name: string;
  short_description?: string | null;
  category?: string | null;
  category_id?: number | null;
  agent_type?: string | null;
  status?: number | null;
  agent_framework_id?: string | null;
  agent_framework_name?: string | null;
  priority?: number | null;
  instructions_set?: string | null;
  default_prompts?: string | null;
  endpoint?: string | null;
  agent_readme_path?: string | null;
  agent_intro_video_path?: string | null;
  presentation_link?: string | null;
  brochure_link?: string | null;
  resources_link?: string | null;
  tags?: string[] | null;
  efficiency_note?: string | null;
  agent_photo?: string | null;
  agent_icon?: string | null;
  how_to_use?: string | null;
  is_display?: boolean | null;
  is_featured?: boolean | null;
  app_version?: string | null;
  created_date?: string | null;
  updated_date?: string | null;
  last_updated?: string | null;
}

export interface Pagination {
  limit: number;
  offset: number;
  hasMore: boolean;
  total: number;
}

export interface FetchAgentsAdminParams {
  limit?: number;
  offset?: number;
  search?: string;
}

export interface FetchAgentsAdminResponse {
  data: ManagedAgent[];
  pagination: Pagination;
}

export type AgentFormPayload = Partial<
  Omit<ManagedAgent, "id" | "created_date" | "updated_date" | "last_updated">
> & { agent_name: string };

export type AgentUpdatePayload = Partial<
  Omit<ManagedAgent, "id" | "created_date" | "updated_date" | "last_updated">
>;

export interface SetAgentStatusPayload {
  agentId: number;
  status: 0 | 1;
}

// Fetch all agents for admin management (includes hidden/inactive)
export const fetchAgentsAdmin = createAsyncThunk(
  "agentManagement/fetchAgentsAdmin",
  async (params: FetchAgentsAdminParams = {}, { rejectWithValue }) => {
    try {
      const { limit = 100, offset = 0, search = "" } = params;

      const response = await axiosClient.get<FetchAgentsAdminResponse>(
        "/admin/agents",
        { params: { limit, offset, search: search || undefined } }
      );
      return response.data;
    } catch (err: any) {
      return rejectWithValue(
        extractErrorMessage(err, "Failed to fetch agents")
      );
    }
  }
);

// Create a new agent
export const createAgent = createAsyncThunk(
  "agentManagement/createAgent",
  async (payload: AgentFormPayload, { rejectWithValue }) => {
    try {
      const response = await axiosClient.post<ManagedAgent>(
        "/admin/agents",
        payload
      );
      return response.data;
    } catch (err: any) {
      return rejectWithValue(
        extractErrorMessage(err, "Failed to create agent")
      );
    }
  }
);

// Update an existing agent
export const updateAgent = createAsyncThunk(
  "agentManagement/updateAgent",
  async (
    { agentId, data }: { agentId: number; data: AgentUpdatePayload },
    { rejectWithValue }
  ) => {
    try {
      const response = await axiosClient.put<ManagedAgent>(
        `/admin/agents/${agentId}`,
        data
      );
      return response.data;
    } catch (err: any) {
      return rejectWithValue(
        extractErrorMessage(err, "Failed to update agent")
      );
    }
  }
);

// Show or hide an agent without deleting it
export const setAgentStatus = createAsyncThunk(
  "agentManagement/setAgentStatus",
  async ({ agentId, status }: SetAgentStatusPayload, { rejectWithValue }) => {
    try {
      const response = await axiosClient.patch<ManagedAgent>(
        `/admin/agents/${agentId}/status`,
        { status }
      );
      return response.data;
    } catch (err: any) {
      return rejectWithValue(
        extractErrorMessage(err, "Failed to update agent status")
      );
    }
  }
);

// Permanently remove an agent (soft-delete server-side; drops out of every listing)
export const deleteAgent = createAsyncThunk(
  "agentManagement/deleteAgent",
  async (agentId: number, { rejectWithValue }) => {
    try {
      const response = await axiosClient.delete<ManagedAgent>(
        `/admin/agents/${agentId}`
      );
      return response.data;
    } catch (err: any) {
      return rejectWithValue(
        extractErrorMessage(err, "Failed to delete agent")
      );
    }
  }
);

interface AgentManagementState {
  agents: ManagedAgent[];
  pagination: Pagination | null;
  loading: boolean;
  error: string | null;
  saveLoading: boolean;
  saveSuccess: boolean;
  saveError: string | null;
  statusUpdatingAgentId: number | null;
  statusError: string | null;
  deletingAgentId: number | null;
  deleteError: string | null;
}

const initialState: AgentManagementState = {
  agents: [],
  pagination: null,
  loading: false,
  error: null,
  saveLoading: false,
  saveSuccess: false,
  saveError: null,
  statusUpdatingAgentId: null,
  statusError: null,
  deletingAgentId: null,
  deleteError: null,
};

const agentManagementSlice = createSlice({
  name: "agentManagement",
  initialState,
  reducers: {
    resetSaveState: (state) => {
      state.saveSuccess = false;
      state.saveError = null;
    },
    resetStatusError: (state) => {
      state.statusError = null;
    },
    resetDeleteError: (state) => {
      state.deleteError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch agents (admin)
      .addCase(fetchAgentsAdmin.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAgentsAdmin.fulfilled, (state, action) => {
        state.loading = false;
        state.agents = action.payload.data;
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchAgentsAdmin.rejected, (state, action) => {
        state.loading = false;
        state.error =
          (action.payload as string) ||
          action.error.message ||
          "Failed to fetch agents";
      })
      // Create agent
      .addCase(createAgent.pending, (state) => {
        state.saveLoading = true;
        state.saveSuccess = false;
        state.saveError = null;
      })
      .addCase(createAgent.fulfilled, (state, action) => {
        state.saveLoading = false;
        state.saveSuccess = true;
        state.agents.unshift(action.payload);
        if (state.pagination) {
          state.pagination.total += 1;
        }
      })
      .addCase(createAgent.rejected, (state, action) => {
        state.saveLoading = false;
        state.saveSuccess = false;
        state.saveError =
          (action.payload as string) ||
          action.error.message ||
          "Failed to create agent";
      })
      // Update agent
      .addCase(updateAgent.pending, (state) => {
        state.saveLoading = true;
        state.saveSuccess = false;
        state.saveError = null;
      })
      .addCase(updateAgent.fulfilled, (state, action) => {
        state.saveLoading = false;
        state.saveSuccess = true;
        const index = state.agents.findIndex(
          (agent) => agent.id === action.payload.id
        );
        if (index !== -1) {
          state.agents[index] = action.payload;
        }
      })
      .addCase(updateAgent.rejected, (state, action) => {
        state.saveLoading = false;
        state.saveSuccess = false;
        state.saveError =
          (action.payload as string) ||
          action.error.message ||
          "Failed to update agent";
      })
      // Set agent status (hide/show)
      .addCase(setAgentStatus.pending, (state, action) => {
        state.statusUpdatingAgentId = action.meta.arg.agentId;
        state.statusError = null;
      })
      .addCase(setAgentStatus.fulfilled, (state, action) => {
        state.statusUpdatingAgentId = null;
        const index = state.agents.findIndex(
          (agent) => agent.id === action.payload.id
        );
        if (index !== -1) {
          state.agents[index] = action.payload;
        }
      })
      .addCase(setAgentStatus.rejected, (state, action) => {
        state.statusUpdatingAgentId = null;
        state.statusError =
          (action.payload as string) ||
          action.error.message ||
          "Failed to update agent status";
      })
      // Delete agent
      .addCase(deleteAgent.pending, (state, action) => {
        state.deletingAgentId = action.meta.arg;
        state.deleteError = null;
      })
      .addCase(deleteAgent.fulfilled, (state, action) => {
        state.deletingAgentId = null;
        state.agents = state.agents.filter(
          (agent) => agent.id !== action.payload.id
        );
        if (state.pagination) {
          state.pagination.total = Math.max(0, state.pagination.total - 1);
        }
      })
      .addCase(deleteAgent.rejected, (state, action) => {
        state.deletingAgentId = null;
        state.deleteError =
          (action.payload as string) ||
          action.error.message ||
          "Failed to delete agent";
      });
  },
});

export const {
  resetSaveState,
  resetStatusError,
  resetDeleteError,
} = agentManagementSlice.actions;
export default agentManagementSlice.reducer;

// ---- Agent Lookups (Category master data) ----

export interface AgentLookup {
  category_id: number;
  category: string;
  created_date?: string | null;
}

// Fetch all category lookup values
export const fetchAgentLookups = createAsyncThunk(
  "agentLookup/fetchAgentLookups",
  async (_: void, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get<AgentLookup[]>(
        "/admin/agent-lookups"
      );
      return response.data;
    } catch (err: any) {
      return rejectWithValue(
        extractErrorMessage(err, "Failed to fetch lookups")
      );
    }
  }
);

// Add a new category value
export const createAgentLookup = createAsyncThunk(
  "agentLookup/createAgentLookup",
  async (payload: { category: string }, { rejectWithValue }) => {
    try {
      const response = await axiosClient.post<AgentLookup>(
        "/admin/agent-lookups",
        payload
      );
      return response.data;
    } catch (err: any) {
      return rejectWithValue(
        extractErrorMessage(err, "Failed to add value")
      );
    }
  }
);

// Rename a category value
export const updateAgentLookup = createAsyncThunk(
  "agentLookup/updateAgentLookup",
  async (
    payload: { categoryId: number; category: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await axiosClient.put<AgentLookup>(
        `/admin/agent-lookups/${payload.categoryId}`,
        { category: payload.category }
      );
      return response.data;
    } catch (err: any) {
      return rejectWithValue(
        extractErrorMessage(err, "Failed to rename category")
      );
    }
  }
);

// Remove a category value
export const deleteAgentLookup = createAsyncThunk(
  "agentLookup/deleteAgentLookup",
  async (categoryId: number, { rejectWithValue }) => {
    try {
      await axiosClient.delete(`/admin/agent-lookups/${categoryId}`);
      return categoryId;
    } catch (err: any) {
      return rejectWithValue(
        extractErrorMessage(err, "Failed to remove value")
      );
    }
  }
);

interface AgentLookupState {
  items: AgentLookup[];
  loading: boolean;
  error: string | null;
  saving: boolean;
  saveError: string | null;
  updatingCategoryId: number | null;
  updateError: string | null;
  deletingCategoryId: number | null;
  deleteError: string | null;
}

const agentLookupInitialState: AgentLookupState = {
  items: [],
  loading: false,
  error: null,
  saving: false,
  saveError: null,
  updatingCategoryId: null,
  updateError: null,
  deletingCategoryId: null,
  deleteError: null,
};

const agentLookupSlice = createSlice({
  name: "agentLookup",
  initialState: agentLookupInitialState,
  reducers: {
    resetLookupSaveError: (state) => {
      state.saveError = null;
    },
    resetLookupDeleteError: (state) => {
      state.deleteError = null;
    },
    resetLookupUpdateError: (state) => {
      state.updateError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAgentLookups.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAgentLookups.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload;
      })
      .addCase(fetchAgentLookups.rejected, (state, action) => {
        state.loading = false;
        state.error =
          (action.payload as string) ||
          action.error.message ||
          "Failed to fetch lookups";
      })
      .addCase(createAgentLookup.pending, (state) => {
        state.saving = true;
        state.saveError = null;
      })
      .addCase(createAgentLookup.fulfilled, (state, action) => {
        state.saving = false;
        const exists = state.items.some(
          (item) => item.category_id === action.payload.category_id
        );
        if (!exists) {
          state.items.push(action.payload);
        }
      })
      .addCase(createAgentLookup.rejected, (state, action) => {
        state.saving = false;
        state.saveError =
          (action.payload as string) ||
          action.error.message ||
          "Failed to add value";
      })
      .addCase(updateAgentLookup.pending, (state, action) => {
        state.updatingCategoryId = action.meta.arg.categoryId;
        state.updateError = null;
      })
      .addCase(updateAgentLookup.fulfilled, (state, action) => {
        state.updatingCategoryId = null;
        const index = state.items.findIndex(
          (item) => item.category_id === action.payload.category_id
        );
        if (index !== -1) {
          state.items[index] = action.payload;
        }
      })
      .addCase(updateAgentLookup.rejected, (state, action) => {
        state.updatingCategoryId = null;
        state.updateError =
          (action.payload as string) ||
          action.error.message ||
          "Failed to rename category";
      })
      .addCase(deleteAgentLookup.pending, (state, action) => {
        state.deletingCategoryId = action.meta.arg;
        state.deleteError = null;
      })
      .addCase(deleteAgentLookup.fulfilled, (state, action) => {
        state.deletingCategoryId = null;
        state.items = state.items.filter(
          (item) => item.category_id !== action.payload
        );
      })
      .addCase(deleteAgentLookup.rejected, (state, action) => {
        state.deletingCategoryId = null;
        state.deleteError =
          (action.payload as string) ||
          action.error.message ||
          "Failed to remove value";
      });
  },
});

export const { resetLookupSaveError, resetLookupDeleteError, resetLookupUpdateError } =
  agentLookupSlice.actions;
export const agentLookupReducer = agentLookupSlice.reducer;
